/**
 * Helper utilities to handle the store's timezone (Europe/Paris)
 * regardless of where the client's or admin's browser is located.
 */

// Seuls ces formats sont reconnus : un format absent de la liste afficherait l'heure à la place
// (« 00:00 » pour une date à minuit). Le type empêche donc d'en passer un autre.
export type ParisDatePattern =
    | "yyyy"
    | "d"
    | "MMM"
    | "MM-dd"
    | "yyyy-MM-dd"
    | "yyyy-MM-dd'T'HH:mm"
    | "HH:mm"
    | "d MMM yyyy"
    | "d MMM yyyy à HH:mm"
    | "d MMMM yyyy"
    | "d MMMM"
    | "EEEE d MMMM"
    | "EEEE d MMMM yyyy"
    | "EEEE, d MMMM";

export function formatInParis(date: Date | null | undefined, pattern: ParisDatePattern): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Paris",
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    });
    const parts = formatter.formatToParts(date);
    const p = Object.fromEntries(parts.map(x => [x.type, x.value]));

    const monthsLong = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
    const monthsShort = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

    const mIdx = parseInt(p.month, 10) - 1;
    const dVal = parseInt(p.day, 10);
    
    // Get the localized weekday in Paris
    const weekdayFormatter = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long" });
    const weekdayLong = weekdayFormatter.format(date);

    const formatMap: Record<ParisDatePattern, string> = {
        "yyyy": p.year,
        "d": String(dVal),
        "MMM": monthsShort[mIdx],
        "MM-dd": `${p.month}-${p.day}`,
        "yyyy-MM-dd": `${p.year}-${p.month}-${p.day}`,
        "yyyy-MM-dd'T'HH:mm": `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`,
        "HH:mm": `${p.hour}:${p.minute}`,
        "d MMM yyyy": `${dVal} ${monthsShort[mIdx]} ${p.year}`,
        "d MMM yyyy à HH:mm": `${dVal} ${monthsShort[mIdx]} ${p.year} à ${p.hour}:${p.minute}`,
        "d MMMM yyyy": `${dVal} ${monthsLong[mIdx]} ${p.year}`,
        "d MMMM": `${dVal} ${monthsLong[mIdx]}`,
        "EEEE d MMMM": `${weekdayLong} ${dVal} ${monthsLong[mIdx]}`,
        "EEEE d MMMM yyyy": `${weekdayLong} ${dVal} ${monthsLong[mIdx]} ${p.year}`,
        "EEEE, d MMMM": `${weekdayLong}, ${dVal} ${monthsLong[mIdx]}`,
    };

    return formatMap[pattern];
}

export function parseInParis(dateStr: string): Date {
    // dateStr can be "yyyy-MM-ddTHH:mm" or "yyyy-MM-dd HH:mm"
    const cleaned = dateStr.replace(" ", "T");
    const utcDate = new Date(cleaned + "Z");
    if (isNaN(utcDate.getTime())) return new Date(dateStr);

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Paris",
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
    
    const parts = formatter.formatToParts(utcDate);
    const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
    
    const formattedLocal = new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00Z`);
    const diffMs = formattedLocal.getTime() - utcDate.getTime();
    
    return new Date(utcDate.getTime() - diffMs);
}
